import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import { getClientProfile, updateClientProfile, DataProfile } from '@/services/clientProfile';
import SEO from '@/components/common/SEO';

export interface RacketMetricDef {
  key: string;
  label: string;
  icon: string;
  bg: string;
  text: string;
  getValue: (statItem: any) => number | string;
}

export const ALL_RACKET_METRICS_LIST: RacketMetricDef[] = [
  { key: 'total_match', label: 'Total match', icon: 'ph:flag-banner-bold', bg: 'bg-[#FEF6E6]', text: 'text-[#D97706]', getValue: (s) => s?.total_match ?? s?.games ?? s?.total_matches ?? 0 },
  { key: 'poin', label: 'Jumlah poin', icon: 'ph:target-bold', bg: 'bg-[#ECF2FE]', text: 'text-[#1D4ED8]', getValue: (s) => s?.poin ?? s?.total_poin ?? s?.points ?? 0 },
  { key: 'point_loss', label: 'Point loss', icon: 'ph:arrow-u-up-left-bold', bg: 'bg-[#ECF2FE]', text: 'text-[#1D4ED8]', getValue: (s) => s?.point_loss ?? s?.loss ?? 0 },
  { key: 'total_menang', label: 'Total menang', icon: 'ph:trophy-bold', bg: 'bg-amber-50', text: 'text-amber-600', getValue: (s) => s?.total_menang ?? s?.wins ?? 0 },
  { key: 'total_kalah', label: 'Total kalah', icon: 'ph:x-circle-bold', bg: 'bg-red-50', text: 'text-red-500', getValue: (s) => s?.total_kalah ?? s?.losses ?? 0 },
  { key: 'winrate', label: 'Winrate', icon: 'ph:percent-bold', bg: 'bg-[#ECF2FE]', text: 'text-[#1D4ED8]', getValue: (s) => s?.win_rate !== undefined ? `${s.win_rate}%` : '0%' },
  { key: 'winstreak', label: 'Best winstreak', icon: 'ph:flame-bold', bg: 'bg-orange-50', text: 'text-orange-500', getValue: (s) => s?.winstreak ?? s?.best_winstreak ?? 0 },
];

export interface ClientRacketStatsSettingsPageProps {
  initialProfile?: DataProfile | null;
}

export const ClientRacketStatsSettingsPage: React.FC<ClientRacketStatsSettingsPageProps> = ({ initialProfile }) => {
  const navigate = useNavigate();

  const [profile, setProfile] = useState<DataProfile | null>(initialProfile || null);
  const [loading, setLoading] = useState(!initialProfile);
  const [isSaving, setIsSaving] = useState(false);

  const [activeSport, setActiveSport] = useState<string>('badminton');
  const [sportConfigs, setSportConfigs] = useState<Record<string, string[]>>({});

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!initialProfile) {
          setLoading(true);
        }
        const res = await getClientProfile();
        if (res.success && res.data) {
          setProfile(res.data);
          const backendConfig = res.data.information?.stat_config || {};
          const defaultRacketFallback = ['poin', 'point_loss', 'total_menang', 'total_kalah'];

          const rawActive: string[] = res.data.information?.active_racket_sports;
          const racketActiveList: string[] =
            Array.isArray(rawActive) && rawActive.length > 0
              ? rawActive
              : ['badminton', 'padel', 'tenis'];

          const initialSport = racketActiveList[0] || 'badminton';
          setActiveSport(initialSport);

          setSportConfigs({
            badminton: backendConfig?.badminton || JSON.parse(localStorage.getItem('sporto_stat_config_badminton') || 'null') || defaultRacketFallback,
            padel: backendConfig?.padel || JSON.parse(localStorage.getItem('sporto_stat_config_padel') || 'null') || defaultRacketFallback,
            tenis: backendConfig?.tenis || JSON.parse(localStorage.getItem('sporto_stat_config_tenis') || 'null') || defaultRacketFallback,
          });
        }
      } catch (err) {
        console.error('Failed to load profile for racket stats settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [initialProfile]);

  const rawActiveRacketSports: string[] = profile?.information?.active_racket_sports;
  const activeRacketSports: string[] =
    Array.isArray(rawActiveRacketSports) && rawActiveRacketSports.length > 0
      ? rawActiveRacketSports
      : ['badminton', 'padel', 'tenis'];

  const availableSportTabs = [
    { id: 'badminton', label: 'Badminton' },
    { id: 'padel', label: 'Padel' },
    { id: 'tenis', label: 'Tenis' },
  ].filter((tab) => activeRacketSports.includes(tab.id));

  const currentKeys = sportConfigs[activeSport] || ['poin', 'point_loss', 'total_menang', 'total_kalah'];
  const sportStatItem = profile?.sport_stats?.[activeSport];

  const handleToggleMetric = (key: string) => {
    const isSelected = currentKeys.includes(key);
    let newKeys: string[];

    if (isSelected) {
      newKeys = currentKeys.filter((k) => k !== key);
    } else {
      if (currentKeys.length >= 4) {
        showToast.error('Maksimal pilih 4 metrik statistik');
        return;
      }
      newKeys = [...currentKeys, key];
    }

    setSportConfigs((prev) => ({
      ...prev,
      [activeSport]: newKeys,
    }));
  };

  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    const newSelected = [...currentKeys];
    const temp = newSelected[index - 1];
    newSelected[index - 1] = newSelected[index];
    newSelected[index] = temp;

    setSportConfigs((prev) => ({
      ...prev,
      [activeSport]: newSelected,
    }));
  };

  const handleMoveDown = (index: number) => {
    if (index >= currentKeys.length - 1) return;
    const newSelected = [...currentKeys];
    const temp = newSelected[index + 1];
    newSelected[index + 1] = newSelected[index];
    newSelected[index] = temp;

    setSportConfigs((prev) => ({
      ...prev,
      [activeSport]: newSelected,
    }));
  };

  const handleSave = async () => {
    for (const sportObj of availableSportTabs) {
      const keys = sportConfigs[sportObj.id] || [];
      if (keys.length !== 4) {
        showToast.error(`Pilih tepat 4 metrik untuk ${sportObj.label}`);
        return;
      }
    }

    try {
      setIsSaving(true);
      const updatedInfo = {
        ...(profile?.information || {}),
        stat_config: {
          ...(profile?.information?.stat_config || {}),
          ...sportConfigs,
        },
      };

      await updateClientProfile({
        information: updatedInfo,
      });

      Object.keys(sportConfigs).forEach((sp) => {
        localStorage.setItem(`sporto_stat_config_${sp}`, JSON.stringify(sportConfigs[sp]));
      });

      showToast.success('Pengaturan statistik racket berhasil disimpan!');
      setTimeout(() => {
        navigate(-1);
      }, 500);
    } catch (err: any) {
      showToast.error(err?.response?.data?.message || 'Gagal menyimpan statistik');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] pb-28 select-none flex flex-col">
      <SEO title="Informasi Statistik Racket | Sporto" description="Pengaturan metrik statistik Racket Game Sporto" />

      {/* Desktop Header */}
      <div className="hidden md:flex items-center gap-3 max-w-md w-full mx-auto pt-4 pb-2 px-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white border border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-gray-50 transition-colors cursor-pointer shadow-2xs"
        >
          <Icon icon="ph:arrow-left-bold" className="w-5 h-5 text-[#0E1726]" />
        </button>
        <h1 className="text-lg md:text-xl font-bold text-[#0E1726]">Informasi Statistik</h1>
      </div>

      {/* Main Content */}
      <div className="max-w-md w-full mx-auto px-4 pt-3 flex-1 space-y-5 animate-fade-in">
        {/* Template Olahraga Section */}
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-500 block">Template olahraga</label>
          <div className="grid grid-cols-3 gap-2">
            {availableSportTabs.map((sport) => {
              const isActive = activeSport === sport.id;
              return (
                <button
                  key={sport.id}
                  type="button"
                  onClick={() => setActiveSport(sport.id)}
                  className={`h-11 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center ${
                    isActive
                      ? 'bg-[#1D4ED8] text-white shadow-sm font-extrabold'
                      : 'bg-white text-[#0E1726] border border-[#E0E6ED] hover:bg-gray-50 font-semibold'
                  }`}
                >
                  {sport.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Metrik Selection Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-500">
              Pilih tepat 4 metrik statistik ({currentKeys.length}/4)
            </span>
          </div>

          <div className="space-y-2.5">
            {ALL_RACKET_METRICS_LIST.map((metric) => {
              const isChecked = currentKeys.includes(metric.key);
              const val = metric.getValue(sportStatItem);

              return (
                <div
                  key={metric.key}
                  onClick={() => handleToggleMetric(metric.key)}
                  className={`bg-white rounded-2xl p-3.5 border transition-all flex items-center justify-between cursor-pointer ${
                    isChecked
                      ? 'border-blue-200 shadow-2xs'
                      : 'border-[#E0E6ED] opacity-70 hover:opacity-100'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Checkbox */}
                    <div
                      className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors shrink-0 ${
                        isChecked ? 'bg-[#1D4ED8] text-white' : 'border border-gray-300 bg-white'
                      }`}
                    >
                      {isChecked && <Icon icon="ph:check-bold" className="w-3.5 h-3.5 stroke-[3]" />}
                    </div>

                    {/* Metric Icon */}
                    <div className={`w-9 h-9 rounded-xl ${metric.bg} ${metric.text} flex items-center justify-center shrink-0`}>
                      <Icon icon={metric.icon} className="w-5 h-5" />
                    </div>

                    {/* Metric Label */}
                    <span className={`text-sm font-bold truncate ${isChecked ? 'text-[#0E1726]' : 'text-gray-500'}`}>
                      {metric.label}
                    </span>
                  </div>

                  {/* Value */}
                  <span className="text-sm font-bold text-gray-400">
                    {loading ? '-' : val}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Urutan Tampil Section */}
        {currentKeys.length > 0 && (
          <div className="space-y-2 pt-2">
            <span className="text-xs font-bold text-gray-500 block">Urutan tampil</span>

            <div className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs divide-y divide-gray-100 overflow-hidden">
              {currentKeys.map((key, idx) => {
                const metricDef = ALL_RACKET_METRICS_LIST.find((m) => m.key === key);
                if (!metricDef) return null;

                return (
                  <div key={key} className="p-3.5 flex items-center justify-between">
                    <span className="text-sm font-bold text-[#0E1726]">{metricDef.label}</span>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleMoveUp(idx)}
                        disabled={idx === 0}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors cursor-pointer"
                        aria-label="Geser ke atas"
                      >
                        <Icon icon="ph:caret-up-bold" className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveDown(idx)}
                        disabled={idx === currentKeys.length - 1}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors cursor-pointer"
                        aria-label="Geser ke bawah"
                      >
                        <Icon icon="ph:caret-down-bold" className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Sticky Bottom Save Button */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#E0E6ED] px-4 pt-4 pb-safe-4 shadow-lg">
        <div className="max-w-md mx-auto">
          <button
            type="button"
            onClick={handleSave}
            disabled={currentKeys.length !== 4 || isSaving}
            className={`w-full h-12 rounded-full font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-md ${
              currentKeys.length === 4 && !isSaving
                ? 'bg-[#1D4ED8] hover:bg-[#1E40AF] text-white cursor-pointer active:scale-98 shadow-blue-200'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
            }`}
          >
            {isSaving ? (
              <>
                <Icon icon="ph:spinner-gap-bold" className="w-4.5 h-4.5 animate-spin" />
                <span>Menyimpan Perubahan...</span>
              </>
            ) : (
              <span>Simpan perubahan</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientRacketStatsSettingsPage;
